// Phase 6A — inline status-transition buttons for each appointment row.
//
// The parent decides which buttons to show via `canEdit` (salespeople
// get only Complete + No-show on their own appointments; managers get
// all of them). This component renders what's available, hits the API,
// toasts, and asks the parent to refetch.
import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { appointmentsApi } from '../../api';

const call = async (fn, id) => {
  const res = await fn(id);
  return res;
};

export default function AppointmentStatusActions({ appointment, canEdit, onChanged, isEditor }) {
  const [busy, setBusy] = useState(null);
  const apt = appointment || {};
  const status = apt.status;
  const needsReview = apt.needs_review === true;
  // Phase 6C — if the parent hasn't specified isEditor, default to true so
  // this component stays self-sufficient in tests / ad-hoc renders.
  const showPhase6C = isEditor === undefined ? true : isEditor === true;
  const confirmationAlreadySent = Boolean(apt.confirmation_sent_at);
  const reminderAlreadySent = Boolean(apt.reminder_sent_at);
  const reminderSkipped = apt.reminder_status === 'skipped';
  const reminderDisabled = reminderAlreadySent || reminderSkipped;

  const run = async (key, apiCall, successMessage) => {
    setBusy(key);
    try {
      await call(apiCall, apt.id);
      toast.success(successMessage);
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err?.message || 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  // Always-visible action: Approve review (when needed). Limited roles
  // (canEdit=false) can still request it via their manager — so we
  // still hide when not editable.
  const showConfirm = canEdit && status === 'scheduled';
  const showComplete = status === 'scheduled' || status === 'confirmed';
  const showNoShow = status === 'scheduled' || status === 'confirmed';
  const showCancel = canEdit && (status === 'scheduled' || status === 'confirmed');
  const showApproveReview = canEdit && needsReview;

  if (!showConfirm && !showComplete && !showNoShow && !showCancel && !showApproveReview && !showPhase6C) {
    return null;
  }

  // Phase 6C — calendar sync / confirmation / reminder handlers.
  // These call different endpoints than the status transitions above,
  // and the "success message" is the status string returned by the API.
  const runPhase6C = async (key, apiCall) => {
    setBusy(key);
    try {
      const result = await apiCall(apt.id);
      toast.success(result?.status || 'Done');
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err?.message || 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col items-end gap-2">
    <div className="flex items-center gap-2 flex-wrap">
      {showConfirm && (
        <Button
          size="sm"
          variant="outline"
          disabled={busy === 'confirm'}
          onClick={() => run('confirm', appointmentsApi.confirm, 'Appointment confirmed')}
          data-testid={`appt-confirm-${apt.id}`}
        >
          Confirm
        </Button>
      )}
      {showComplete && (
        <Button
          size="sm"
          variant="outline"
          disabled={busy === 'complete'}
          onClick={() => run('complete', appointmentsApi.complete, 'Appointment marked complete')}
          data-testid={`appt-complete-${apt.id}`}
        >
          Complete
        </Button>
      )}
      {showNoShow && (
        <Button
          size="sm"
          variant="outline"
          disabled={busy === 'noShow'}
          onClick={() => run('noShow', appointmentsApi.noShow, 'Marked no-show')}
          data-testid={`appt-noshow-${apt.id}`}
        >
          No-show
        </Button>
      )}
      {showCancel && (
        <Button
          size="sm"
          variant="outline"
          disabled={busy === 'cancel'}
          onClick={() => run('cancel', appointmentsApi.cancel, 'Appointment cancelled')}
          data-testid={`appt-cancel-${apt.id}`}
        >
          Cancel
        </Button>
      )}
      {showApproveReview && (
        <Button
          size="sm"
          variant="default"
          disabled={busy === 'approveReview'}
          onClick={() => run('approveReview', appointmentsApi.approveReview, 'Review approved')}
          data-testid={`appt-approve-review-${apt.id}`}
        >
          Approve review
        </Button>
      )}
    </div>
    {/* Phase 6C — second row: calendar sync + messaging actions. Editors only. */}
    {showPhase6C && (
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          variant="outline"
          disabled={busy === 'syncCalendar'}
          onClick={() => runPhase6C('syncCalendar', appointmentsApi.syncCalendar)}
          data-testid={`appt-sync-calendar-${apt.id}`}
        >
          Sync calendar
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy === 'sendConfirmation' || confirmationAlreadySent}
          title={confirmationAlreadySent ? 'Already sent' : undefined}
          onClick={() => runPhase6C('sendConfirmation', appointmentsApi.sendConfirmation)}
          data-testid={`appt-send-confirmation-${apt.id}`}
        >
          Send confirmation
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy === 'sendReminder' || reminderDisabled}
          title={
            reminderAlreadySent
              ? 'Already sent'
              : reminderSkipped
                ? 'Reminder skipped'
                : undefined
          }
          onClick={() => runPhase6C('sendReminder', appointmentsApi.sendReminder)}
          data-testid={`appt-send-reminder-${apt.id}`}
        >
          Send reminder
        </Button>
      </div>
    )}
    </div>
  );
}
